import { useCallback, useEffect, useState } from 'react'

import { SortOrder } from '@avantstay/arriere-clients/dist/arriereBackoffice'

export enum SortingDirection {
  Desc = 'desc',
  Asc = 'asc',
}

interface UseHandleTableSort<SortBy> {
  value?: string
  sortOption: SortBy
  sortBy: (sortBy?: SortBy) => void
  sortOrder: (sortOrder?: SortOrder) => void
}

interface UseHandleTableSortResult {
  columnDirection?: SortingDirection
  handleTableSort: (sort: SortingDirection) => void
}

const useHandleTableSort = <SortBy>({
  value,
  sortOption,
  sortBy,
  sortOrder,
}: UseHandleTableSort<SortBy>): UseHandleTableSortResult => {
  const [columnDirection, setColumnDirection] = useState<SortingDirection | undefined>(undefined)

  const isCurrentSortOption = value?.includes(String(sortOption))

  useEffect(() => {
    if (value?.includes(SortOrder.ascending) && isCurrentSortOption) {
      setColumnDirection(SortingDirection.Asc)
    }

    if (value?.includes(SortOrder.descending) && isCurrentSortOption) {
      setColumnDirection(SortingDirection.Desc)
    }

    if (value !== undefined && !isCurrentSortOption) {
      setColumnDirection(undefined)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const handleTableSort = useCallback(
    (sort: SortingDirection) => {
      if (columnDirection === sort && isCurrentSortOption) {
        setColumnDirection(undefined)
        sortBy(undefined)
        sortOrder(undefined)
        return
      }

      setColumnDirection(sort)
      sortBy(sortOption)
      sortOrder(sort === SortingDirection.Asc ? SortOrder.ascending : SortOrder.descending)
    },
    [sortOption, columnDirection, sortBy, sortOrder, isCurrentSortOption],
  )

  return { columnDirection, handleTableSort }
}

export { useHandleTableSort }
